import { db } from '../config/firebase';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  deleteDoc,
  query,
  where,
  Timestamp,
  onSnapshot,
  QuerySnapshot,
} from 'firebase/firestore';
import { Reaction, ReactionType, ReactionsByType, ReactionCount } from '../types/reactions';

class ReactionsService {
  private db = db;
  private readonly collectionName = 'reactions';

  async addReaction(
    goodThingId: string,
    userId: string,
    userDisplayName: string,
    reactionType: ReactionType
  ): Promise<void> {
    const reactionId = `${goodThingId}_${userId}`;
    const reactionRef = doc(this.db, this.collectionName, reactionId);

    const reaction: Reaction = {
      goodThingId,
      userId,
      userDisplayName,
      reactionType,
      createdAt: Timestamp.now(),
    };

    await setDoc(reactionRef, reaction);
  }

  async removeReaction(goodThingId: string, userId: string): Promise<void> {
    const reactionId = `${goodThingId}_${userId}`;
    const reactionRef = doc(this.db, this.collectionName, reactionId);
    await deleteDoc(reactionRef);
  }

  async getUserReaction(goodThingId: string, userId: string): Promise<Reaction | null> {
    const reactionId = `${goodThingId}_${userId}`;
    const reactionRef = doc(this.db, this.collectionName, reactionId);
    const docSnap = await getDoc(reactionRef);
    
    return docSnap.exists() ? (docSnap.data() as Reaction) : null;
  }

  async getReactionsByGoodThing(goodThingId: string): Promise<ReactionsByType> {
    const reactionsRef = collection(this.db, this.collectionName);
    const q = query(reactionsRef, where('goodThingId', '==', goodThingId));
    const querySnapshot = await getDocs(q);

    return this.processReactionsSnapshot(querySnapshot);
  }

  subscribeToReactions(
    goodThingId: string,
    callback: (reactions: ReactionsByType) => void
  ): () => void {
    const reactionsRef = collection(this.db, this.collectionName);
    const q = query(reactionsRef, where('goodThingId', '==', goodThingId));

    return onSnapshot(q, (snapshot) => {
      const reactions = this.processReactionsSnapshot(snapshot);
      callback(reactions);
    });
  }

  private processReactionsSnapshot(snapshot: QuerySnapshot): ReactionsByType {
    const reactions = snapshot.docs.map(doc => doc.data() as Reaction);
    
    // Initialize the reactions object with empty counts for all reaction types
    const reactionsByType = {} as ReactionsByType;
    
    // Process each reaction and group by type
    reactions.forEach(reaction => {
      if (!reactionsByType[reaction.reactionType]) {
        reactionsByType[reaction.reactionType] = {
          count: 0,
          users: [],
        };
      }
      
      reactionsByType[reaction.reactionType].count++;
      reactionsByType[reaction.reactionType].users.push({
        id: reaction.userId,
        displayName: reaction.userDisplayName,
      });
    });

    return reactionsByType;
  }
}

export const reactionsService = new ReactionsService();
export default reactionsService;